/* eslint-disable react/button-has-type */

import classNames from "classnames";
import { LoaderCircle } from "lucide-react";
import {
  ButtonHTMLAttributes, cloneElement, isValidElement, ReactElement, ReactNode,
} from "react";
import HideIf from "util/HideIf";
import styles from "./button.module.scss";

export type ButtonVariant =
  | "primary"
  | "danger"
  | "success"
  | "neutral"
  | "brand"
  | "primary-outline"
  | "danger-outline"
  | "success-outline"
  | "neutral-outline"
  | "brand-outline";

export type ButtonSize = "sm" | "md" | "lg";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary,
  danger: styles.danger,
  success: styles.success,
  neutral: styles.neutral,
  brand: styles.brand,
  "primary-outline": styles.primary_outline,
  "danger-outline": styles.danger_outline,
  "success-outline": styles.success_outline,
  "neutral-outline": styles.neutral_outline,
  "brand-outline": styles.brand_outline,
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  pill?: boolean;
  bold?: boolean;
  uppercase?: boolean;
  boxShadow?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Render the single child element with button styles (e.g. a Next `<Link>`).
   *  Icons/loading are not composed in this mode — put content inside the child. */
  asChild?: boolean;
  children: ReactNode;
}

function Button(props: IButtonProps) {
  const {
    variant = "primary", size = "md", type = "button", className = "",
    pill = false, bold = false, uppercase = false, boxShadow = false, fullWidth = false, loading = false,
    leftIcon, rightIcon, asChild = false, disabled, children, ...restProps
  } = props;

  const rootClassName = classNames(
    styles.base,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    className,
    {
      [styles.pill]: pill,
      [styles.bold]: bold,
      [styles.uppercase]: uppercase,
      [styles.boxShadow]: boxShadow,
      [styles.fullWidth]: fullWidth,
    },
  );

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ className?: string }>;
    return cloneElement(child, {
      ...restProps,
      className: classNames(rootClassName, child.props.className),
    });
  }

  return (
    <button
      type={type}
      className={rootClassName}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...restProps}
    >
      <HideIf show={loading}>
        <LoaderCircle size={18} className={styles.spinner} />
      </HideIf>
      <HideIf show={!loading && Boolean(leftIcon)}>
        {leftIcon}
      </HideIf>
      <span className={styles.label}>{children}</span>
      <HideIf show={Boolean(rightIcon)}>
        {rightIcon}
      </HideIf>
    </button>
  );
}

export default Button;
